import * as React from "react";
import { Text } from "@fluentui/react/lib/Text";
import { Icon } from "@fluentui/react/lib/Icon";
import { Input } from "@mui/material";
import { sellPriceFromMarginPercent } from "@shared/utils/marginOnSell";

interface LTLOfferCardProps {
  offerId: string;
  carrierName: string;
  serviceLevel: string;
  price: number;
  transitDays: string | number;
  scac?: string;
  vendorId?: string;
  isSelected: boolean;
  onToggle: () => void;
  isBestRate?: boolean;
  marginMode?: "percent" | "dollar";
  marginPct?: number;
  marginDollars?: number;
  onMarginPctChange?: (marginPct: number) => void;
  onMarginDollarsChange?: (marginDollars: number) => void;
  onMarginModeChange?: (mode: "percent" | "dollar") => void;
}
const formatCurrency = (value: number | undefined | null) =>
  (Number.isFinite(Number(value)) ? Number(value) : 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const cleanCarrierName = (name: string | undefined | null) => {
  return (name ?? "")
    .replace(/\s+(LLC|INC|CO|LTD)$/i, "")
    .replace(/\s+/g, " ")
    .trim();
};
const roundToTwoDecimals = (value: number) => Math.round(value * 100) / 100;
const marginArrowButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 22,
  height: 22,
  padding: 0,
  border: "1px solid #c8c6c4",
  borderRadius: 2,
  background: "#fff",
  cursor: "pointer",
  color: "#323130",
};
const segmentBtn = (active: boolean): React.CSSProperties => ({
  minWidth: 36,
  padding: "6px 10px",
  border: "1px solid #c8c6c4",
  background: active ? "#edebe9" : "#fff",
  fontWeight: active ? 600 : 400,
  fontSize: "13px",
  cursor: "pointer",
});

export default function LTLOfferCard({
  carrierName,
  serviceLevel,
  price,
  transitDays,
  scac,
  vendorId,
  isSelected,
  onToggle,
  isBestRate = false,
  marginMode = "percent",
  marginPct = 0,
  marginDollars = 0,
  onMarginPctChange,
  onMarginDollarsChange,
  onMarginModeChange,
}: LTLOfferCardProps) {
  const pct =
    typeof marginPct === "number" && !Number.isNaN(marginPct) ? Math.max(0, marginPct) : 0;
  const dollars =
    typeof marginDollars === "number" && !Number.isNaN(marginDollars)
      ? Math.max(0, marginDollars)
      : 0;
  const effectivePrice =
    marginMode === "dollar"
      ? roundToTwoDecimals(price + dollars)
      : sellPriceFromMarginPercent(price, pct);
  const marginPctInputValue = pct !== 0 ? pct : "";
  const marginDollarInputValue = dollars !== 0 ? dollars : "";

  return (
    <div
      className="offer-card"
      onClick={onToggle}
      style={{
        padding: "16px 20px",
        border: isSelected ? "2px solid #0078d4" : "1px solid #e1e1e1",
        borderRadius: "8px",
        marginBottom: "12px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        backgroundColor: isSelected ? "#f8f9ff" : "#ffffff",
        transform: isSelected ? "translateY(-1px)" : "translateY(0)",
        boxShadow: isSelected ? "0 2px 8px rgba(0, 120, 212, 0.1)" : "0 1px 3px rgba(0, 0, 0, 0.1)",
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.1)";
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
        }
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1 }}>
            {isBestRate && (
              <div
                style={{
                  display: "inline-block",
                  backgroundColor: "#107c41",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: "600",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  marginBottom: "8px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Best Price
              </div>
            )}

            <Text
              variant="medium"
              block
              style={{
                fontSize: "14px",
                fontWeight: "600",
                color: "#323130",
                lineHeight: "20px",
                marginBottom: "4px",
              }}
            >
              {cleanCarrierName(carrierName)} — {serviceLevel}
            </Text>

            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <Text
                  variant="small"
                  style={{
                    fontSize: "12px",
                    color: "#605e5c",
                    fontWeight: "400",
                  }}
                >
                  {transitDays} day transit
                </Text>
                {(scac || vendorId) && (
                  <Text
                    variant="small"
                    style={{
                      fontSize: "12px",
                      color: "#605e5c",
                      fontWeight: "400",
                    }}
                  >
                    • {scac || vendorId}
                  </Text>
                )}
              </div>
            </div>
          </div>

          <Text
            variant="large"
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#323130",
              lineHeight: "24px",
              marginLeft: "16px",
              flexShrink: 0,
            }}
          >
            {formatCurrency(effectivePrice)}
          </Text>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            width: "100%",
            minWidth: 0,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Text
            variant="small"
            style={{
              fontSize: "12px",
              color: "#323130",
              fontWeight: "400",
              flexShrink: 0,
            }}
          >
            {formatCurrency(price)} +
          </Text>
          <Text
            variant="small"
            style={{
              fontSize: "12px",
              color: "#323130",
              fontWeight: "400",
              flexShrink: 0,
            }}
          >
            Margin
          </Text>
          <div style={{ display: "flex", marginLeft: "auto", flexShrink: 0 }}>
            <button
              type="button"
              style={{
                ...segmentBtn(marginMode === "dollar"),
                borderRadius: "4px 0 0 4px",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMarginModeChange?.("dollar");
              }}
            >
              $
            </button>
            <button
              type="button"
              style={{
                ...segmentBtn(marginMode === "percent"),
                borderRadius: "0 4px 4px 0",
                borderLeft: "none",
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onMarginModeChange?.("percent");
              }}
            >
              %
            </button>
          </div>
          {marginMode === "percent" ? (
            <>
              <Input
                type="number"
                value={marginPctInputValue}
                placeholder="0"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const next = parseFloat(e.target.value);

                  onMarginPctChange?.(Number.isNaN(next) ? 0 : roundToTwoDecimals(next));
                }}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  width: 44,
                  "& input": {
                    padding: "0 4px",
                    fontSize: "14px",
                    textAlign: "right",
                    color: "#323130",
                    "&::placeholder": {
                      color: "#605e5c",
                      opacity: 1,
                    },
                    MozAppearance: "textfield",
                    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                      WebkitAppearance: "none",
                      margin: 0,
                    },
                  },
                  "& fieldset": {
                    borderColor: "#c8c6c4",
                  },
                }}
              />
              <Text
                variant="small"
                style={{
                  fontSize: "12px",
                  color: "#323130",
                  fontWeight: "400",
                  flexShrink: 0,
                }}
              >
                %
              </Text>
            </>
          ) : (
            <Input
              type="number"
              value={marginDollarInputValue}
              placeholder="0"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const next = parseFloat(e.target.value);

                onMarginDollarsChange?.(Number.isNaN(next) ? 0 : roundToTwoDecimals(next));
              }}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{
                width: 72,
                "& input": {
                  padding: "0 4px",
                  fontSize: "14px",
                  textAlign: "right",
                  color: "#323130",
                  "&::placeholder": {
                    color: "#605e5c",
                    opacity: 1,
                  },
                  MozAppearance: "textfield",
                  "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                },
                "& fieldset": {
                  borderColor: "#c8c6c4",
                },
              }}
            />
          )}
          <span style={{ width: "8px", flexShrink: 0 }} aria-hidden />
          <button
            type="button"
            aria-label="Increase margin"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (marginMode === "percent") {
                onMarginPctChange?.(roundToTwoDecimals(pct + 0.5));
              } else {
                onMarginDollarsChange?.(roundToTwoDecimals(dollars + 0.5));
              }
            }}
            style={marginArrowButtonStyle}
          >
            <Icon iconName="ChevronUp" styles={{ root: { fontSize: 12 } }} />
          </button>
          <button
            type="button"
            aria-label="Decrease margin"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (marginMode === "percent") {
                onMarginPctChange?.(roundToTwoDecimals(Math.max(0, pct - 0.5)));
              } else {
                onMarginDollarsChange?.(roundToTwoDecimals(Math.max(0, dollars - 0.5)));
              }
            }}
            style={marginArrowButtonStyle}
          >
            <Icon iconName="ChevronDown" styles={{ root: { fontSize: 12 } }} />
          </button>
        </div>
      </div>
    </div>
  );
}
